import { Link } from "react-router-dom";
import { ArrowRight, Accessibility, Wifi, Tv, ShowerHead, Briefcase, Wind, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { Reveal, Img, Container, SectionTitle, HScroll, Chip, PhotoHero } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { SIZES } from "@/lib/images";

export const FACILITIES = [
  { icon: ShowerHead, nn: "Bad med dusj", en: "Bathroom with shower" },
  { icon: Wifi, nn: "Trådlaust nett", en: "Wireless internet" },
  { icon: Tv, nn: "TV og telefon", en: "TV and phone" },
  { icon: Briefcase, nn: "Arbeidspult", en: "Desk" },
  { icon: Wind, nn: "Hårtørkar og vasskokar", en: "Hairdryer and kettle" },
];

const BUILDING_IMAGES = {
  fjordhotell: { src: "/images/autumn-fjord-hotel.jpg", position: "70% 50%" },
  elvafloy: { src: "/images/elvafloy-exterior.jpg", position: "50% 50%" },
  midtfloy: { src: "/images/hotel-entrance.jpg", position: "50% 40%" },
};

export default function Rooms() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const isMobile = useIsMobile();
  const buildings = data?.buildings || [];
  const roomTypes = data?.room_types || [];
  usePageMeta(tx("Rom", "Rooms"), tx("Rom i tre hus ved Fitjarvika. Frukost i Fjordhotellet er inkludert i alle opphald.", "Rooms in three houses on Fitjarvika. Breakfast in the Fjord Hotel is included in every stay."));

  const gallery = [
    { src: "/images/view-balcony.jpg", caption: tx("Balkong på Fjordhotellet", "Balcony at the Fjord Hotel"), meta: "Fjordhotellet" },
    { src: "/images/room-superior-double.jpg", caption: tx("Superiorrom på Fjordhotellet", "Superior room at the Fjord Hotel"), meta: "Fjordhotellet", aspect: "aspect-[4/3]", w: "w-[84vw] sm:w-[56vw] lg:w-[520px]" },
    { src: "/images/room-elvafloy-attic.jpg", caption: tx("Rom under skråtaket på Elvafløy", "Room under the eaves in the River Wing"), meta: "Elvafløy" },
    { src: "/images/autumn-river-cascades.jpg", caption: tx("Fitjarelva utanfor Elvafløy", "The Fitjar river outside the River Wing"), meta: "Elvafløy", aspect: "aspect-[4/3]", w: "w-[84vw] sm:w-[56vw] lg:w-[520px]", position: "50% 60%" },
    { src: "/images/room-midtfloy-desk.jpg", caption: tx("Arbeidspult på Midtfløy", "Desk in the Middle Wing"), meta: "Midtfløy" },
    { src: "/images/bathroom-shower.jpg", caption: tx("Bad med dusj", "Bathroom with shower"), meta: tx("Alle rom", "Every room") },
  ];

  return (
    <div data-testid="rooms-page">
      <PhotoHero
        testid="rooms-hero"
        image="/images/hotel-fjord.jpg"
        position="62% 50%"
        alt={tx("Fjordhotellet med Fitjarvika bak", "The Fjord Hotel with Fitjarvika behind")}
        kicker={tx("Tre hus · 61 rom", "Three houses · 61 rooms")}
        title={tx("Rom i tre hus ved Fitjarvika", "Rooms in three houses on Fitjarvika")}
        lead={tx("Vel mellom Fjordhotellet ved sjøen, Elvafløy ved Fitjarelva og eineromma på Midtfløy. Frukost i Fjordhotellet er inkludert i alle opphald.", "Choose between the Fjord Hotel by the sea, the River Wing by the Fitjar river and the single rooms in the Middle Wing. Breakfast in the Fjord Hotel is included in every stay.")}
        height="h-[72svh] min-h-[520px] max-h-[820px]"
      >
        {!isMobile && (
          <div className="mt-9 w-full max-w-[980px]" data-sticky-anchor>
            <BookingBar testid="rooms-booking-bar" />
          </div>
        )}
      </PhotoHero>

      <Container className="mt-8 lg:mt-10">
        <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:px-0" data-testid="rooms-building-nav">
          {buildings.map((b) => (
            <Chip key={b.id} as="a" href={`#${b.id}`} testid={`rooms-nav-${b.id}`}>{tr(b.name)}</Chip>
          ))}
        </div>
      </Container>

      {/* buildings */}
      {buildings.map((b, bi) => {
        const rooms = roomTypes.filter((r) => r.building === b.id);
        const img = BUILDING_IMAGES[b.id] || { src: b.image };
        const from = rooms.length ? Math.min(...rooms.map(minPrice)) : null;
        return (
          <section key={b.id} id={b.id} className={`scroll-mt-28 ${bi === 0 ? "pt-14 lg:pt-20" : "pt-20 lg:pt-32"}`} data-testid={`rooms-building-${b.id}`}>
            <Container>
              <Reveal className="grid gap-6 lg:grid-cols-12 lg:items-center lg:gap-12">
                <div className="lg:col-span-7">
                  <p className="text-sm font-medium text-muted-foreground">{tr(b.tagline)}</p>
                  <h2 className="mt-2 text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem] lg:text-[2.75rem]">{tr(b.name)}</h2>
                  <p className="mt-4 max-w-[58ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tr(b.description)}</p>
                  <p className="mt-5 text-[0.9375rem] text-foreground tabular">
                    {rooms.length} {tx(rooms.length === 1 ? "romtype" : "romtypar", rooms.length === 1 ? "room type" : "room types")}
                    {from && <span className="text-muted-foreground"> · {tx("frå", "from")} <span className="font-semibold text-foreground">{nok(from, lang)}</span> {tx("per natt", "per night")}</span>}
                  </p>
                </div>
                <div className="lg:col-span-5">
                  <Img src={img.src} alt={tr(b.name)} className="aspect-[16/10]" radius="rounded-[24px]" position={img.position} sizes={SIZES.half} />
                </div>
              </Reveal>
              <div className="mt-8 grid gap-x-7 gap-y-10 sm:grid-cols-2 lg:mt-10 lg:grid-cols-3" data-testid={`rooms-building-${b.id}-list`}>
                {rooms.map((rt, i) => (
                  <Reveal key={rt.id} delay={Math.min(i, 2) * 0.05}><RoomCard rt={rt} testid={`room-row-${rt.id}`} eager={bi === 0 && i < 3} /></Reveal>
                ))}
              </div>
            </Container>
          </section>
        );
      })}

      {/* In the room */}
      <section className="mt-20 bg-secondary/70 py-20 lg:mt-32 lg:py-32" data-testid="rooms-facilities">
        <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionTitle size="lg" title={tx("På rommet", "In the room")} />
            <ul className="mt-8 grid gap-x-6 gap-y-5 sm:grid-cols-2">
              {FACILITIES.map(({ icon: I, nn, en }) => (
                <li key={nn} className="flex items-center gap-3.5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-card text-foreground ring-hairline"><I className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden /></span>
                  <span className="text-[0.9375rem] font-medium leading-snug text-foreground">{tx(nn, en)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 border-t border-border pt-6">
              <p className="text-sm font-semibold text-foreground">{tx("Inkludert i opphaldet", "Included in your stay")}</p>
              <p className="mt-1.5 max-w-[52ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Frukost i Fjordhotellet, parkering ved hotellet og kaffi i resepsjonen.", "Breakfast in the Fjord Hotel, parking at the hotel and coffee in reception.")}</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[24px] bg-card p-7 ring-hairline sm:p-8" data-testid="rooms-stepfree">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-foreground"><Accessibility className="h-5 w-5" strokeWidth={1.75} /></span>
              <h3 className="mt-5 text-[1.375rem] leading-tight tracking-[-0.02em] text-foreground">{tx("Treng du eit rom utan trapper?", "Do you need a step-free room?")}</h3>
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-muted-foreground">{tx("Ta kontakt før du bestiller, så kan me sjekke om eit av kombiromma er ledig. Fortel gjerne om andre behov, så får du vite kva rom som kan passe.", "Get in touch before you book, and we will check whether one of the twin rooms is available. Tell us about any other needs too, and we will let you know which rooms could suit.")}</p>
              <Button asChild variant="soft" className="mt-6" data-testid="rooms-stepfree-call"><a href="tel:+4753457700"><Phone className="h-4 w-4" /> 53 45 77 00</a></Button>
            </div>
          </div>
        </Container>
      </section>

      {/* Gallery */}
      <section className="py-20 lg:py-32" data-testid="rooms-gallery-section">
        <Container>
          <SectionTitle size="lg" title={tx("Sjå romma", "See the rooms")} />
          <Reveal className="mt-10"><HScroll items={gallery} testid="rooms-gallery" /></Reveal>
        </Container>
      </section>

      {/* CTA */}
      <section className="pb-20 lg:pb-32" data-testid="rooms-cta">
        <Container>
          <div className="flex flex-col gap-6 border-t border-border pt-14 lg:flex-row lg:items-end lg:justify-between lg:pt-16">
            <div>
              <h2 className="text-[1.75rem] leading-[1.05] tracking-[-0.03em] text-foreground sm:text-[2.125rem]">{tx("Treng du hjelp til å velje rom?", "Need help choosing a room?")}</h2>
              <p className="mt-3 max-w-[50ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Ring oss på 53 45 77 00, eller spør Fjordverten om dei ulike romtypane.", "Call us on +47 53 45 77 00, or ask Fjordverten about the different room types.")}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="soft" size="lg" onClick={() => ask(null)} data-testid="rooms-ask-cta"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              <Button asChild size="lg" data-testid="rooms-book-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" /></Link></Button>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
